import React from 'react';
import clsx from 'clsx';
import { DotIcon, UserIcon } from '@/components/icons';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import type { StudentProfileV1 } from '@/types/studentprofile';
import { useI18n } from '@/i18n/I18nProvider';
import { resolveLatestEnrollment } from '@/pages/dashboard/components/StudentCard';

interface StudentProfileCardProps {
  student: StudentProfileV1;
  displayName?: string;
  sourcedId?: string;
  className?: string;
}

export default function StudentProfileCard({
  student,
  displayName: customDisplayName,
  sourcedId,
  className,
}: StudentProfileCardProps) {
  const { locale } = useI18n();

  const latestEnrollment = resolveLatestEnrollment(student.enrollment);
  const educationType = latestEnrollment?.educationType ?? null;
  const isPrivateEducation = educationType?.toLowerCase() === 'private';

  const displayName =
    customDisplayName ||
    (locale === 'ar'
      ? [student.firstNameArabic, student.middleNameArabic, student.lastNameArabic].filter(Boolean).join(' ')
      : [
          student.firstNameEnglish,
          student.middleNameEnglish,
          student.thirdNameEnglish,
          student.fourthNameEnglish,
          student.familyNameEnglish,
        ]
          .filter(Boolean)
          .join(' ')) ||
    sourcedId ||
    student.id;

  const initial = displayName ? displayName.charAt(0).toUpperCase() : '?';

  return (
    <Card className={clsx('border border-primary/20 shadow-sm hover:border-primary/40 mb-4', className)}>
      <CardHeader>
        <CardTitle className="text-base sm:text-lg flex items-center gap-3">
          <div className="relative">
            <div
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center text-primary font-semibold text-lg sm:text-xl ring-2 ring-primary/20 ring-offset-2 ring-offset-background transition-all duration-300 hover:scale-110 hover:ring-primary/40 hover:shadow-lg"
              aria-hidden="true"
            >
              {initial}
            </div>
            {student.status === 'active' && (
              <div className="absolute -bottom-0.5 -right-0.5 md:-bottom-1 md:-right-1 w-5 h-5 md:w-6 md:h-6 bg-green-500 rounded-full border-2 md:border-3 border-background flex items-center justify-center">
                <svg className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            )}
          </div>

          <div className="flex-1">
            <span className="block mb-2">{displayName}</span>
            {/* Enhanced Badges */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="aegov-badge font-medium text-xs border bg-gray-100 text-gray-500">
                <span className="hidden md:inline"># </span>
                {student.studentNumber || student.id.slice(-6)}
              </div>
              <div className="aegov-badge font-medium text-xs border">
                <UserIcon size={14} />
                <span className="capitalize">{student.role || (locale === 'ar' ? 'طالب' : 'Student')}</span>
              </div>

              {isPrivateEducation ? (
                <div className="aegov-badge font-medium text-xs border bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-950 dark:text-violet-300 dark:border-violet-800">
                  {locale === 'ar' ? 'خاص' : 'Private'}
                </div>
              ) : (
                student.isActive !== undefined && (
                  <div
                    className={clsx(
                      'aegov-badge font-medium text-xs border',
                      student.isActive ? 'badge-success' : 'bg-gray-100 text-gray-500'
                    )}
                  >
                    <DotIcon size={14} />
                    <span>
                      {student.isActive ? (locale === 'ar' ? 'نشط' : 'Active') : locale === 'ar' ? 'غير نشط' : 'Inactive'}
                    </span>
                  </div>
                )
              )}
            </div>
          </div>
        </CardTitle>
      </CardHeader>
    </Card>
  );
}
